import { useState } from 'react';
import type { Poll } from '@gl/shared';

import { useSavePoll } from '../api/hooks.js';
import { Modal } from './Modal.js';

/**
 * Writing a poll, new or existing.
 *
 * The same form does both, because they are the same form: an editor who
 * mistypes a question or an option wants to fix that wording, not delete the
 * poll and ask it again. Editing the options is the one destructive part — the
 * server replaces them, and the votes go with them — so it is spelled out on
 * screen rather than discovered afterwards.
 */
export function PollEditor({ poll, onClose }: { poll?: Poll; onClose: () => void }) {
  const [question, setQuestion] = useState(poll?.question ?? '');
  const [description, setDescription] = useState(poll?.description ?? '');
  const [multiSelect, setMultiSelect] = useState(poll?.multiSelect ?? false);
  const [options, setOptions] = useState(poll ? poll.options.map((o) => o.text) : ['', '']);
  const save = useSavePoll();

  const filled = options.map((option) => option.trim()).filter(Boolean);
  const valid = question.trim().length > 0 && filled.length >= 2;

  const editing = poll !== undefined;
  // Only send options when they actually changed: an unchanged list would still
  // be replaced row for row, taking every vote with it.
  const optionsChanged =
    !editing ||
    filled.length !== poll.options.length ||
    filled.some((text, index) => text !== poll.options[index]?.text);

  return (
    <Modal
      open
      title={editing ? 'Edit poll' : 'New poll'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!valid || save.isPending}
            onClick={() => {
              const written = {
                question: question.trim(),
                description: description.trim() || null,
                multiSelect,
              };
              save.mutate(
                poll
                  ? {
                      id: poll.id,
                      input: optionsChanged ? { ...written, options: filled } : written,
                    }
                  : { input: { ...written, options: filled } },
                { onSuccess: onClose },
              );
            }}
          >
            {save.isPending
              ? editing
                ? 'Saving…'
                : 'Creating…'
              : editing
                ? 'Save changes'
                : 'Create poll'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="label" htmlFor="poll-question">
            Question
          </label>
          <input
            id="poll-question"
            className="input"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
          />
        </div>

        <div>
          <label className="label" htmlFor="poll-description">
            Description <span className="text-field-500">(optional)</span>
          </label>
          <textarea
            id="poll-description"
            className="input min-h-[4rem]"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-field-200">
          <input
            type="checkbox"
            className="h-4 w-4 accent-brass-500"
            checked={multiSelect}
            onChange={(event) => setMultiSelect(event.target.checked)}
          />
          Allow more than one choice
        </label>

        <div>
          <span className="label">Options</span>
          <div className="space-y-2">
            {options.map((option, index) => (
              <div key={index} className="flex gap-2">
                <input
                  className="input"
                  placeholder={`Option ${index + 1}`}
                  value={option}
                  onChange={(event) =>
                    setOptions(options.map((v, i) => (i === index ? event.target.value : v)))
                  }
                />
                {options.length > 2 ? (
                  <button
                    type="button"
                    className="btn-ghost px-2"
                    aria-label={`Remove option ${index + 1}`}
                    onClick={() => setOptions(options.filter((_, i) => i !== index))}
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            ))}
          </div>
          {options.length < 20 ? (
            <button
              type="button"
              className="btn-ghost mt-2 px-2 py-1 text-xs"
              onClick={() => setOptions([...options, ''])}
            >
              + Add option
            </button>
          ) : null}
          {editing && optionsChanged && poll.totalVotes > 0 ? (
            <p className="mt-2 border-l-2 border-signal pl-2 text-xs text-signal">
              Changing the options clears the {poll.totalVotes}{' '}
              {poll.totalVotes === 1 ? 'vote' : 'votes'} already cast — people are no longer
              choosing between the same things. Editing only the wording above keeps them.
            </p>
          ) : null}
        </div>

        {save.error ? <p className="text-sm text-signal">{(save.error as Error).message}</p> : null}
      </div>
    </Modal>
  );
}
